import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMaintenance } from '../contexts/MaintenanceContext';
import { UserRole } from '../types/auth';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
}

const ProtectedRoute = ({ 
  children, 
  allowedRoles = ['admin', 'student'] 
}: ProtectedRouteProps) => {
  const { authState } = useAuth();
  const { isAuthenticated, user, loading } = authState;
  const { isMaintenanceMode, isAllowedUser } = useMaintenance();
  const location = useLocation();

  // If still loading, return nothing to avoid flashing content
  if (loading) {
    return null;
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if maintenance mode is active and user is a student
  // Special routes like /coming-soon should be excluded from this check
  // Allow whitelisted emails to bypass maintenance mode
  if (isMaintenanceMode && 
      user.role === 'student' && 
      location.pathname !== '/coming-soon') {
    
    console.log('======= MAINTENANCE MODE CHECK IN PROTECTED ROUTE =======');
    console.log(`User: ${user.email} (Role: ${user.role})`);
    console.log(`Current path: ${location.pathname}`);
    console.log(`Maintenance mode: ${isMaintenanceMode ? 'ENABLED' : 'DISABLED'}`);
    
    // Check if user is allowed past maintenance mode
    const allowed = isAllowedUser(user.email);
    console.log(`Bypass allowed: ${allowed ? 'YES' : 'NO'}`);
    
    if (!allowed) {
      console.log('=> Redirecting to maintenance page');
      console.log('=======================================================');
      return <Navigate to="/coming-soon" replace />;
    }
    
    console.log('=> Allowing access to requested page');
    console.log('=======================================================');
  }

  // Check if user has the required role
  if (!allowedRoles.includes(user.role)) {
    // Redirect to dashboard or unauthorized page based on role
    if (user.role === 'student') {
      return <Navigate to="/dashboard" replace />;
    } else if (user.role === 'admin') {
      return <Navigate to="/admin" replace />;
    } else if (user.role === 'super_admin') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/unauthorized" replace />;
  }

  // If everything is fine, render the children
  return <>{children}</>;
};

export default ProtectedRoute;